import { useState, useEffect, useMemo } from "react";
import { useToast } from "../../contexts/ToastContext";
import {
  useGetRoomWeeklyAvailabilityQuery,
  useSaveRoomWeeklyAvailabilityMutation,
  useSaveRoomScheduleSettingsMutation,
} from "../../store/services/roomsApi";
import {
  getWeekId,
  getWeekStart,
  formatLocalDateString,
  HEBREW_DAYS_WITH_APOSTROPHE,
} from "../../utils/dateUtils";
import { WEEKDAY_KEYS } from "../../utils/roomAvailability";
import { getHolidayForDateStr } from "../../utils/israeliHolidays";
import { addWeeks } from "date-fns";
import {
  CalendarBlank,
  CaretLeft,
  CaretRight,
  FloppyDisk,
  Info,
  Plus,
  Prohibit,
  Trash,
  ArrowCounterClockwise,
  MagicWand,
} from "@phosphor-icons/react";
import TimePicker from "../ui/TimePicker";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./RoomScheduleEditor.css";

// Current week + 52 more = a full year of exception planning ahead.
const MAX_WEEK_OFFSET = 52;

const DEFAULT_SLOT = { start: "08:00", end: "20:00" };

// Normalize a room's scheduleTemplate (or null) into a full 7-day map so the
// editor never has to null-check individual days.
const normalizeTemplate = (tpl) => {
  const out = {};
  WEEKDAY_KEYS.forEach((key) => {
    out[key] = { slots: (tpl?.[key]?.slots || []).map((s) => ({ ...s })) };
  });
  return out;
};

/**
 * RoomScheduleEditor — modal for a room's availability:
 *   1. Toggles: always-available + auto holiday block.
 *   2. Recurring weekly template (7 day columns, Sunday first).
 *   3. Per-week exceptions that override the template for specific dates.
 * Presentation + save orchestration only; resolution logic lives in
 * src/utils/roomAvailability.js.
 */
function RoomScheduleEditor({
  clinicId,
  room,
  onClose,
  therapistId,
  therapistName,
}) {
  const { showSuccess, showError } = useToast();

  const [saveScheduleSettings] = useSaveRoomScheduleSettingsMutation();
  const [saveWeeklyAvailability] = useSaveRoomWeeklyAvailabilityMutation();

  // ── Settings state (toggles + recurring template) ──────────────────────
  const [alwaysAvailable, setAlwaysAvailable] = useState(
    room?.alwaysAvailable === true,
  );
  const [blockOnHolidays, setBlockOnHolidays] = useState(
    room?.blockOnHolidays === true,
  );
  const [template, setTemplate] = useState(() =>
    normalizeTemplate(room?.scheduleTemplate),
  );
  // Distinguishes "room has a recurring template" from "template is empty".
  // Becomes true the moment the user edits template hours (or converts).
  const [templateExists, setTemplateExists] = useState(
    !!room?.scheduleTemplate,
  );
  const [settingsDirty, setSettingsDirty] = useState(false);

  // ── Week navigation ────────────────────────────────────────────────────
  const [weekOffset, setWeekOffset] = useState(0);

  const viewedWeekStart = useMemo(
    () => getWeekStart(addWeeks(new Date(), weekOffset)),
    [weekOffset],
  );
  const viewedWeekId = useMemo(
    () => getWeekId(viewedWeekStart),
    [viewedWeekStart],
  );

  const weekDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(viewedWeekStart);
      date.setDate(viewedWeekStart.getDate() + i);
      days.push({
        date: formatLocalDateString(date),
        dayIndex: date.getDay(),
        dayLabel: HEBREW_DAYS_WITH_APOSTROPHE[date.getDay()],
        dateLabel: `${date.getDate()}/${date.getMonth() + 1}`,
        isPast: date < today,
      });
    }
    return days;
  }, [viewedWeekStart]);

  const { data: weekDoc, isFetching: isFetchingWeek } =
    useGetRoomWeeklyAvailabilityQuery(
      { clinicId, roomId: room?.id, weekId: viewedWeekId },
      { skip: !clinicId || !room?.id },
    );

  // Per-week local edits: { [weekId]: { [dateStr]: {closed:true} | {slots:[…]} } }.
  // Only dates that DEVIATE from the template get an entry.
  const [weeksData, setWeeksData] = useState({});
  const [dirtyWeeks, setDirtyWeeks] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Hydrate a fetched week doc into the local map — keyed by the doc's own
  // weekId so fast navigation can't mis-assign, and never over local edits.
  useEffect(() => {
    if (!weekDoc?.weekId) return;
    const { weekId, slots } = weekDoc;
    setWeeksData((prev) => {
      if (prev[weekId] !== undefined || dirtyWeeks[weekId]) return prev;
      const map = {};
      (slots || []).forEach((entry) => {
        if (!entry?.date) return;
        map[entry.date] =
          entry.closed === true || !(entry.slots || []).length
            ? { closed: true }
            : { slots: entry.slots.map((s) => ({ ...s })) };
      });
      return { ...prev, [weekId]: map };
    });
  }, [weekDoc, dirtyWeeks]);

  const viewedExceptions = weeksData[viewedWeekId] || {};

  const anyDirty = settingsDirty || Object.keys(dirtyWeeks).length > 0;

  // ── Template editing ───────────────────────────────────────────────────
  const editTemplateDay = (dayKey, updater) => {
    setTemplate((prev) => ({
      ...prev,
      [dayKey]: { slots: updater(prev[dayKey]?.slots || []) },
    }));
    setTemplateExists(true);
    setSettingsDirty(true);
  };

  const addTemplateSlot = (dayKey) =>
    editTemplateDay(dayKey, (slots) => [...slots, { ...DEFAULT_SLOT }]);

  const removeTemplateSlot = (dayKey, index) =>
    editTemplateDay(dayKey, (slots) => slots.filter((_, i) => i !== index));

  const updateTemplateSlot = (dayKey, index, field, value) =>
    editTemplateDay(dayKey, (slots) =>
      slots.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );

  // ── Exception editing (viewed week) ────────────────────────────────────
  const markWeekDirty = () =>
    setDirtyWeeks((prev) =>
      prev[viewedWeekId] ? prev : { ...prev, [viewedWeekId]: true },
    );

  const setException = (dateStr, entryOrNull) => {
    setWeeksData((prev) => {
      const week = { ...(prev[viewedWeekId] || {}) };
      if (entryOrNull === null) delete week[dateStr];
      else week[dateStr] = entryOrNull;
      return { ...prev, [viewedWeekId]: week };
    });
    markWeekDirty();
  };

  // "התאם שעות לשבוע זה" — start an editable exception prefilled from the
  // template (or a sensible default when the room has no template).
  const createException = (day) => {
    const baseline = templateExists
      ? template[WEEKDAY_KEYS[day.dayIndex]]?.slots || []
      : [];
    const slots = baseline.length
      ? baseline.map((s) => ({ ...s }))
      : [{ ...DEFAULT_SLOT }];
    setException(day.date, { slots });
  };

  const closeDay = (day) => setException(day.date, { closed: true });

  const clearException = (day) => setException(day.date, null);

  const addExceptionSlot = (dateStr) => {
    const entry = viewedExceptions[dateStr];
    setException(dateStr, {
      slots: [...(entry?.slots || []), { ...DEFAULT_SLOT }],
    });
  };

  const removeExceptionSlot = (dateStr, index) => {
    const entry = viewedExceptions[dateStr];
    setException(dateStr, {
      slots: (entry?.slots || []).filter((_, i) => i !== index),
    });
  };

  const updateExceptionSlot = (dateStr, index, field, value) => {
    const entry = viewedExceptions[dateStr];
    setException(dateStr, {
      slots: (entry?.slots || []).map((s, i) =>
        i === index ? { ...s, [field]: value } : s,
      ),
    });
  };

  // ── Legacy conversion ──────────────────────────────────────────────────
  // Room has week docs but no recurring template — offer to seed the template
  // from the currently displayed week (client-side; saved on "שמירה").
  const isLegacy = !templateExists;
  const legacyHasWeekData = Object.keys(viewedExceptions).length > 0;

  const handleConvertToTemplate = () => {
    const next = normalizeTemplate(null);
    weekDays.forEach((day) => {
      const entry = viewedExceptions[day.date];
      if (entry && !entry.closed && (entry.slots || []).length) {
        next[WEEKDAY_KEYS[day.dayIndex]] = {
          slots: entry.slots.map((s) => ({ ...s })),
        };
      }
    });
    setTemplate(next);
    setTemplateExists(true);
    setSettingsDirty(true);
  };

  // ── Baseline (what applies when a date has no exception) ───────────────
  const baselineForDay = (day) => {
    const holiday = getHolidayForDateStr(day.date);
    if (holiday?.severity === "red" && blockOnHolidays) {
      return { type: "holiday", holiday };
    }
    if (templateExists) {
      const slots = template[WEEKDAY_KEYS[day.dayIndex]]?.slots || [];
      return slots.length
        ? { type: "slots", slots, holiday }
        : { type: "closed", holiday };
    }
    if (legacyHasWeekData) return { type: "closed", holiday };
    return { type: "open", holiday };
  };

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!anyDirty || isSaving) return;
    setIsSaving(true);
    let allOk = true;

    // (a) toggles + recurring template
    if (settingsDirty) {
      try {
        const settings = { alwaysAvailable, blockOnHolidays };
        if (templateExists) {
          const hasAnySlots = WEEKDAY_KEYS.some(
            (k) => (template[k]?.slots || []).length > 0,
          );
          // An all-empty template on a room that never had one is "no
          // template" (null), not "closed every day".
          settings.scheduleTemplate =
            hasAnySlots || room?.scheduleTemplate ? template : null;
        }
        await saveScheduleSettings({
          clinicId,
          roomId: room.id,
          settings,
          therapistId,
          therapistName,
        }).unwrap();
        setSettingsDirty(false);
      } catch (error) {
        console.error("[CK-5410] Error saving room schedule settings:", {
          errorCode: "CK-5410",
          errorMessage: error.message,
          clinicId,
          roomId: room?.id,
          timestamp: new Date().toISOString(),
        });
        showError("שגיאה בשמירת הגדרות הזמינות של החדר");
        allOk = false;
      }
    }

    // (b) each dirty week — with a template, write only deviating dates
    // (incl. explicit closed entries); legacy rooms keep the old "open days
    // only" doc shape.
    const hasTemplateNow = templateExists || !!room?.scheduleTemplate;
    for (const weekId of Object.keys(dirtyWeeks)) {
      try {
        const entries = Object.entries(weeksData[weekId] || {});
        const slots = hasTemplateNow
          ? entries.map(([date, entry]) =>
              entry.closed === true
                ? { date, closed: true, slots: [] }
                : { date, slots: entry.slots || [] },
            )
          : entries
              .filter(
                ([, entry]) => !entry.closed && (entry.slots || []).length > 0,
              )
              .map(([date, entry]) => ({ date, slots: entry.slots }));

        await saveWeeklyAvailability({
          clinicId,
          roomId: room.id,
          weekId,
          slots,
        }).unwrap();

        setDirtyWeeks((prev) => {
          const next = { ...prev };
          delete next[weekId];
          return next;
        });
      } catch (error) {
        console.error("[CK-5411] Error saving room week exceptions:", {
          errorCode: "CK-5411",
          errorMessage: error.message,
          clinicId,
          roomId: room?.id,
          weekId,
          timestamp: new Date().toISOString(),
        });
        showError("שגיאה בשמירת חריגות השבוע");
        allOk = false;
      }
    }

    setIsSaving(false);
    if (allOk) showSuccess("לוח הזמינות של החדר נשמר בהצלחה");
  };

  // ── Render helpers ─────────────────────────────────────────────────────
  const renderSlotRow = (slot, index, { onStart, onEnd, onRemove }) => (
    <div key={index} className="rse-slot">
      <TimePicker
        className="rse-tp"
        value={slot.start}
        onChange={(e) => onStart(e.target.value)}
      />
      <span className="rse-slot-sep">–</span>
      <TimePicker
        className="rse-tp"
        value={slot.end}
        onChange={(e) => onEnd(e.target.value)}
      />
      <button
        type="button"
        className="rse-slot-remove"
        onClick={onRemove}
        title="מחיקת חלון זמן"
        aria-label="מחיקת חלון זמן"
      >
        <Trash size={13} weight="bold" />
      </button>
    </div>
  );

  const renderToggleRow = ({ title, desc, value, onToggle }) => (
    <button
      type="button"
      className={`rse-toggle-row ${value ? "on" : ""}`}
      onClick={onToggle}
    >
      <div className="rse-toggle-text">
        <span className="rse-toggle-title">{title}</span>
        <span className="rse-toggle-desc">{desc}</span>
      </div>
      <span className={`rse-switch ${value ? "active" : ""}`}>
        <span className="rse-switch-knob" />
      </span>
    </button>
  );

  const renderTemplateDay = (dayKey, dayIndex) => {
    const slots = template[dayKey]?.slots || [];
    return (
      <div
        key={dayKey}
        className={`rse-tpl-day ${slots.length ? "active" : ""}`}
      >
        <div className="rse-tpl-day-head">
          <span className="rse-tpl-day-name">
            {HEBREW_DAYS_WITH_APOSTROPHE[dayIndex]}
          </span>
          {slots.length === 0 && <span className="rse-closed-tag">סגור</span>}
        </div>
        <div className="rse-tpl-day-slots">
          {slots.map((slot, index) =>
            renderSlotRow(slot, index, {
              onStart: (v) => updateTemplateSlot(dayKey, index, "start", v),
              onEnd: (v) => updateTemplateSlot(dayKey, index, "end", v),
              onRemove: () => removeTemplateSlot(dayKey, index),
            }),
          )}
          <button
            type="button"
            className="rse-add-btn"
            onClick={() => addTemplateSlot(dayKey)}
          >
            <Plus size={13} weight="bold" />
            <span>חלון</span>
          </button>
        </div>
      </div>
    );
  };

  const renderWeekDay = (day) => {
    const exception = viewedExceptions[day.date];
    const baseline = baselineForDay(day);
    const holiday = baseline.holiday;

    return (
      <div
        key={day.date}
        className={`rse-week-day ${day.isPast ? "past" : ""} ${
          exception ? "exception" : ""
        }`}
      >
        <div className="rse-week-day-label">
          <span className="rse-week-day-name">{day.dayLabel}</span>
          <span className="rse-week-day-date">{day.dateLabel}</span>
          {holiday && (
            <span className={`rse-holiday-badge ${holiday.severity}`}>
              {holiday.name}
            </span>
          )}
          {exception && <span className="rse-exception-badge">חריגה</span>}
        </div>

        <div className="rse-week-day-body">
          {day.isPast ? (
            <span className="rse-muted">יום שעבר</span>
          ) : exception ? (
            exception.closed ? (
              <span className="rse-closed-note">
                <Prohibit size={13} weight="bold" />
                סגור בשבוע זה
              </span>
            ) : (
              <div className="rse-week-day-slots">
                {(exception.slots || []).map((slot, index) =>
                  renderSlotRow(slot, index, {
                    onStart: (v) =>
                      updateExceptionSlot(day.date, index, "start", v),
                    onEnd: (v) => updateExceptionSlot(day.date, index, "end", v),
                    onRemove: () => removeExceptionSlot(day.date, index),
                  }),
                )}
                <button
                  type="button"
                  className="rse-add-btn"
                  onClick={() => addExceptionSlot(day.date)}
                >
                  <Plus size={13} weight="bold" />
                  <span>חלון</span>
                </button>
              </div>
            )
          ) : baseline.type === "holiday" ? (
            <span className="rse-baseline holiday">
              חסום אוטומטית בחג: {baseline.holiday.name}
            </span>
          ) : baseline.type === "slots" ? (
            <span className="rse-baseline">
              {baseline.slots.map((s) => `${s.start}–${s.end}`).join(", ")}
              <span className="rse-baseline-tag">לפי התבנית</span>
            </span>
          ) : baseline.type === "closed" ? (
            <span className="rse-baseline">
              סגור
              {templateExists && (
                <span className="rse-baseline-tag">לפי התבנית</span>
              )}
            </span>
          ) : (
            <span className="rse-baseline">פתוח (ברירת מחדל)</span>
          )}
        </div>

        {!day.isPast && (
          <div className="rse-week-day-actions">
            {exception ? (
              <>
                {!exception.closed && (
                  <button
                    type="button"
                    className="rse-day-action danger"
                    onClick={() => closeDay(day)}
                  >
                    <Prohibit size={12} weight="bold" />
                    סגירת יום
                  </button>
                )}
                <button
                  type="button"
                  className="rse-day-action"
                  onClick={() => clearException(day)}
                >
                  <ArrowCounterClockwise size={12} weight="bold" />
                  חזרה לתבנית
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="rse-day-action"
                  onClick={() => createException(day)}
                >
                  <Plus size={12} weight="bold" />
                  התאם שעות לשבוע זה
                </button>
                <button
                  type="button"
                  className="rse-day-action danger"
                  onClick={() => closeDay(day)}
                >
                  <Prohibit size={12} weight="bold" />
                  סגירת יום
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Modal ──────────────────────────────────────────────────────────────
  return (
    <div className="modal-overlay rse-overlay" onClick={onClose}>
      <div className="rse-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="rse-header">
          <div className="rse-header-title">
            <span className="rse-header-icon">
              <CalendarBlank size={20} weight="fill" />
            </span>
            <div>
              <h2>{room?.name}</h2>
              <p>לוח זמינות</p>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Body */}
        <div className="rse-body">
          {/* Toggles */}
          <div className="rse-toggles">
            {renderToggleRow({
              title: "החדר זמין תמיד (אלא אם הוזמן)",
              desc: "התבנית והחריגות לא ייבדקו — החדר פתוח בכל שעה.",
              value: alwaysAvailable,
              onToggle: () => {
                setAlwaysAvailable((v) => !v);
                setSettingsDirty(true);
              },
            })}
            {renderToggleRow({
              title: "חסימה אוטומטית בחגים",
              desc: "החדר ייחסם אוטומטית בחגים ובימי שבתון.",
              value: blockOnHolidays,
              onToggle: () => {
                setBlockOnHolidays((v) => !v);
                setSettingsDirty(true);
              },
            })}
          </div>

          {/* Legacy conversion hint */}
          {isLegacy && legacyHasWeekData && !alwaysAvailable && (
            <div className="rse-legacy-banner">
              <Info size={18} weight="fill" />
              <span>
                לחדר זה מוגדרות שעות לשבועות בודדים בלבד. מומלץ להגדיר תבנית
                קבועה שתחול על כל שבוע.
              </span>
              <button
                type="button"
                className="rse-legacy-btn"
                onClick={handleConvertToTemplate}
              >
                <MagicWand size={14} weight="bold" />
                המרה לתבנית קבועה
              </button>
            </div>
          )}

          <div className={alwaysAvailable ? "rse-sections disabled" : "rse-sections"}>
            {/* Recurring weekly template */}
            <section className="rse-section">
              <div className="rse-section-head">
                <h3>תבנית שבועית קבועה</h3>
                <p>
                  התבנית חלה על כל שבוע, אלא אם הוגדרה חריגה לשבוע מסוים.
                </p>
              </div>
              <div className="rse-tpl-grid">
                {WEEKDAY_KEYS.map((dayKey, dayIndex) =>
                  renderTemplateDay(dayKey, dayIndex),
                )}
              </div>
            </section>

            {/* Per-week exceptions */}
            <section className="rse-section">
              <div className="rse-section-head">
                <h3>חריגות לשבוע מסוים</h3>
                <p>
                  שינוי שעות, סגירת יום או פתיחה חריגה — לתאריכים ספציפיים
                  בלבד.
                </p>
              </div>

              <div className="rse-week-nav">
                <button
                  type="button"
                  className="rse-week-nav-btn"
                  onClick={() => setWeekOffset((o) => Math.max(0, o - 1))}
                  disabled={weekOffset === 0}
                  title="שבוע קודם"
                  aria-label="שבוע קודם"
                >
                  <CaretRight size={16} weight="bold" />
                </button>

                <div className="rse-week-title">
                  <span className="rse-week-name">
                    {weekOffset === 0
                      ? "השבוע"
                      : weekOffset === 1
                        ? "שבוע הבא"
                        : `בעוד ${weekOffset} שבועות`}
                    {isFetchingWeek && (
                      <LoaderLoop size="xs" />
                    )}
                  </span>
                  <span className="rse-week-range">
                    {viewedWeekStart.toLocaleDateString("he-IL")}
                  </span>
                  {weekOffset > 0 && (
                    <button
                      type="button"
                      className="rse-week-reset"
                      onClick={() => setWeekOffset(0)}
                    >
                      השבוע
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className="rse-week-nav-btn"
                  onClick={() =>
                    setWeekOffset((o) => Math.min(MAX_WEEK_OFFSET, o + 1))
                  }
                  disabled={weekOffset === MAX_WEEK_OFFSET}
                  title="שבוע הבא"
                  aria-label="שבוע הבא"
                >
                  <CaretLeft size={16} weight="bold" />
                </button>
              </div>

              <div className="rse-week-list">
                {weekDays.map((day) => renderWeekDay(day))}
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="rse-footer">
          <span className={`rse-footer-status ${anyDirty ? "dirty" : ""}`}>
            {anyDirty ? (
              <>
                <span className="rse-footer-dot" />
                יש שינויים שלא נשמרו
              </>
            ) : (
              "הכל שמור"
            )}
          </span>
          <button
            type="button"
            className="rse-save-btn"
            onClick={handleSave}
            disabled={!anyDirty || isSaving}
          >
            {isSaving ? (
              <LoaderLoop size="xs" />
            ) : (
              <FloppyDisk size={16} weight="bold" />
            )}
            {isSaving ? "שומר..." : "שמירה"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RoomScheduleEditor;
